import { useCallback } from 'react';
import { cn } from '@/lib/utils';
import { seedFromHex } from '@/lib/polaroid-utils';

/**
 * Post-it note colors — solid, opaque, like real sticky notes.
 */
const POSTIT_COLORS = [
  '#FFF176', // classic yellow
  '#FFF59D', // light yellow
  '#FFE082', // golden
  '#F8BBD0', // pink
  '#B3E5FC', // baby blue
  '#C8E6C9', // mint green
  '#E1BEE7', // lavender
];

/** Dark pen ink — bold, like writing with a Sharpie on a post-it */
const INK = '#2d1b4e';
const INK_MED = 'rgba(45,27,78,0.6)';

/**
 * Hand-drawn warning triangle ⚠ — sketchy, imperfect lines.
 */
function WarningTriangle({ size, color }: { size: number; color: string }) {
  const h = size * 0.9;
  return (
    <svg width={size} height={h} viewBox="0 0 20 18" fill="none" className="pointer-events-none inline-block align-middle">
      <path
        d="M10 2L2 16H18L10 2Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      <line x1="10" y1="7" x2="10" y2="12" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="10" cy="14.2" r="0.9" fill={color} />
    </svg>
  );
}

/**
 * Bold hand-drawn double underline — like someone underlined
 * a heading twice with a pen, slightly wobbly.
 */
function HandDrawnUnderline({ width, color }: { width: number; color: string }) {
  const h = Math.round(width * 0.18);
  return (
    <svg width={width} height={h} viewBox="0 0 80 14" fill="none" className="pointer-events-none">
      {/* First line — slightly wavy */}
      <path
        d="M4 4C12 2.5 20 5.5 28 3.5C36 1.5 44 5 52 3C60 1 68 4.5 76 3"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Second line — offset, also wavy */}
      <path
        d="M4 10C14 8 22 11.5 32 9C42 6.5 52 11 62 8.5C70 6.5 74 9.5 76 9"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * Corner doodle clusters — each is a little composition of shapes,
 * dots, and circles like someone doodled in the margin of a post-it.
 * Drawn with strokes (not fills) so they look hand-drawn, not stamped.
 *
 * There are 6 variants; the component picks one deterministically.
 * The SVG is designed to sit in a corner — shapes radiate inward.
 */
const CORNER_CLUSTERS = [
  // Heart + star + dots
  (c: string) => (
    <svg viewBox="0 0 44 44" fill="none">
      {/* Heart */}
      <path d="M14 28C14 28 8 24 8 20C8 18 9.5 17 11 17C12 17 13 17.5 14 18.5C15 17.5 16 17 17 17C18.5 17 20 18 20 20C20 24 14 28 14 28Z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" />
      {/* Star */}
      <path d="M32 32L33.5 27.5L38 26L33.5 24.5L32 20L30.5 24.5L26 26L30.5 27.5L32 32Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" />
      {/* Dots */}
      <circle cx="24" cy="10" r="1.8" fill={c} />
      <circle cx="8" cy="8" r="1.2" fill={c} />
      <circle cx="18" cy="6" r="0.8" fill={c} />
      {/* Small circle */}
      <circle cx="36" cy="12" r="2.5" stroke={c} strokeWidth="1.2" />
      <circle cx="28" cy="16" r="1" fill={c} />
    </svg>
  ),
  // Flower + sparkle + dots
  (c: string) => (
    <svg viewBox="0 0 44 44" fill="none">
      {/* Flower */}
      <circle cx="14" cy="22" r="3" stroke={c} strokeWidth="1.3" />
      <circle cx="14" cy="16" r="2.5" stroke={c} strokeWidth="1.2" />
      <circle cx="19" cy="20" r="2.5" stroke={c} strokeWidth="1.2" />
      <circle cx="9" cy="20" r="2.5" stroke={c} strokeWidth="1.2" />
      <circle cx="16" cy="26" r="2.5" stroke={c} strokeWidth="1.2" />
      <circle cx="11" cy="26" r="2.5" stroke={c} strokeWidth="1.2" />
      {/* Sparkle */}
      <path d="M34 30L35 26L38 25L35 24L34 20L33 24L30 25L33 26L34 30Z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      {/* Dots */}
      <circle cx="26" cy="8" r="1.5" fill={c} />
      <circle cx="10" cy="6" r="1" fill={c} />
      <circle cx="32" cy="14" r="0.8" fill={c} />
      <circle cx="22" cy="14" r="1.2" fill={c} />
    </svg>
  ),
  // Star + heart + small marks
  (c: string) => (
    <svg viewBox="0 0 44 44" fill="none">
      {/* Big star */}
      <path d="M16 30L18 22L24 18L18 16L16 8L14 16L8 18L14 22L16 30Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Small heart */}
      <path d="M34 24C34 24 31 22 31 20C31 19 32 18.5 33 18.5C33.5 18.5 34 19 34 19C34 19 34.5 18.5 35 18.5C36 18.5 37 19 37 20C37 22 34 24 34 24Z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      {/* Dots + circles */}
      <circle cx="30" cy="10" r="2" stroke={c} strokeWidth="1.1" />
      <circle cx="24" cy="6" r="1.3" fill={c} />
      <circle cx="36" cy="34" r="1" fill={c} />
      <circle cx="8" cy="36" r="0.8" fill={c} />
      <circle cx="26" cy="32" r="1.5" fill={c} />
    </svg>
  ),
  // Butterfly + dots + circle
  (c: string) => (
    <svg viewBox="0 0 44 44" fill="none">
      {/* Butterfly */}
      <path d="M16 22C16 22 10 16 8 18C6 20 10 24 12 24C14 24 16 22 16 22Z" stroke={c} strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M16 22C16 22 22 16 24 18C26 20 22 24 20 24C18 24 16 22 16 22Z" stroke={c} strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M16 22C16 22 12 26 11 28C10 30 13 28 14 27C15 26 16 22 16 22Z" stroke={c} strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M16 22C16 22 20 26 21 28C22 30 19 28 18 27C17 26 16 22 16 22Z" stroke={c} strokeWidth="1.1" strokeLinejoin="round" />
      <line x1="16" y1="18" x2="16" y2="30" stroke={c} strokeWidth="0.8" />
      {/* Dots + accents */}
      <circle cx="34" cy="30" r="2" stroke={c} strokeWidth="1.1" />
      <circle cx="30" cy="12" r="1.5" fill={c} />
      <circle cx="36" cy="18" r="0.9" fill={c} />
      <circle cx="8" cy="8" r="1.3" fill={c} />
      <circle cx="26" cy="8" r="1" fill={c} />
    </svg>
  ),
  // Diamond + heart + scattered marks
  (c: string) => (
    <svg viewBox="0 0 44 44" fill="none">
      {/* Diamond */}
      <path d="M14 12L22 20L14 28L6 20L14 12Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Small heart */}
      <path d="M32 30C32 30 29 27 29 25C29 24 30 23 31 23C31.5 23 32 23.5 32 23.5C32 23.5 32.5 23 33 23C34 23 35 24 35 25C35 27 32 30 32 30Z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      {/* Dots */}
      <circle cx="30" cy="10" r="1.8" fill={c} />
      <circle cx="36" cy="16" r="1" fill={c} />
      <circle cx="24" cy="6" r="0.8" fill={c} />
      <circle cx="8" cy="36" r="1.2" fill={c} />
      {/* Small circle */}
      <circle cx="34" cy="38" r="2" stroke={c} strokeWidth="1" />
    </svg>
  ),
  // Sparkles + star + dots
  (c: string) => (
    <svg viewBox="0 0 44 44" fill="none">
      {/* Big sparkle */}
      <path d="M14 28L16 18L24 16L16 14L14 4L12 14L4 16L12 18L14 28Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" />
      {/* Small star */}
      <path d="M32 34L33 30L36 29L33 28L32 24L31 28L28 29L31 30L32 34Z" stroke={c} strokeWidth="1.1" strokeLinejoin="round" />
      {/* Dots */}
      <circle cx="30" cy="10" r="1.5" fill={c} />
      <circle cx="36" cy="20" r="1" fill={c} />
      <circle cx="26" cy="16" r="0.8" fill={c} />
      <circle cx="8" cy="36" r="1.3" fill={c} />
      {/* Small circle */}
      <circle cx="22" cy="34" r="2.2" stroke={c} strokeWidth="1" />
    </svg>
  ),
];

/**
 * Renders a corner doodle cluster — a composition of shapes and dots.
 * The `flip` props mirror the cluster to fit any corner.
 */
function CornerCluster({ index, color, size, flipX, flipY }: {
  index: number;
  color: string;
  size: number;
  flipX?: boolean;
  flipY?: boolean;
}) {
  const render = CORNER_CLUSTERS[index % CORNER_CLUSTERS.length];
  return (
    <div
      className="pointer-events-none"
      style={{
        width: size,
        height: size,
        transform: `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`,
      }}
    >
      {render(color)}
    </div>
  );
}

/**
 * Big bold hand-drawn eye with slash — the central doodle.
 * Thick lines like drawn with a marker, not a fine pen.
 */
function EyeSlashDoodle({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 48 34" fill="none" className="pointer-events-none">
      {/* Eye outline — thick, hand-drawn feel */}
      <path
        d="M3 17C8 6 16 2 24 2C32 2 40 6 45 17C40 28 32 32 24 32C16 32 8 28 3 17Z"
        stroke={color}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      {/* Iris */}
      <circle cx="24" cy="17" r="7" stroke={color} strokeWidth="2.2" fill="none" />
      {/* Pupil */}
      <circle cx="24" cy="17" r="2.5" fill={color} />
      {/* Slash — bold diagonal line */}
      <line x1="8" y1="30" x2="40" y2="4" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

interface ContentWarningOverlayProps {
  reason: string | true;
  eventId: string;
  variant?: 'card' | 'detail';
  onReveal: () => void;
  className?: string;
}

/**
 * Scrapbook-style post-it note content warning overlay.
 *
 * Designed to look like someone hand-wrote and doodled on a real
 * post-it note with a dark pen / Sharpie. Elements are BIG and
 * BOLD — they fill the note the way real handwriting does.
 *
 * Layout:
 *   ⚠ CONTENT WARNING  (with warning triangle)
 *   ═══════════════════  (double hand-drawn underline)
 *       Nudity           (BIG reason text)
 *        👁‍🗨            (big bold eye-slash doodle)
 *     tap to reveal      (call to action)
 *
 * Corner doodles (hearts, stars, etc.) scattered around the margins.
 */
export function ContentWarningOverlay({ reason, eventId, variant = 'card', onReveal, className }: ContentWarningOverlayProps) {
  const label = typeof reason === 'string' ? reason : 'Sensitive content';
  const isDetail = variant === 'detail';

  // ── Deterministic seeds ──────────────────────────────────────
  const noteColor = POSTIT_COLORS[seedFromHex(eventId, 30, 34) % POSTIT_COLORS.length];
  const noteRotation = (seedFromHex(eventId, 34, 38) % 9) - 4;
  const offsetX = (seedFromHex(eventId, 38, 40) % 7) - 3;
  const offsetY = (seedFromHex(eventId, 40, 42) % 7) - 3;
  const curlCorner = seedFromHex(eventId, 42, 44) % 2; // 0=BR, 1=BL

  // Pick 2 different corner cluster variants (opposite corners share)
  const cluster1 = seedFromHex(eventId, 44, 48) % CORNER_CLUSTERS.length;
  const cluster2 = (cluster1 + 1 + seedFromHex(eventId, 48, 52) % (CORNER_CLUSTERS.length - 1)) % CORNER_CLUSTERS.length;

  const handleTap = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onReveal();
  }, [onReveal]);

  // Scale everything based on card vs detail
  const d = isDetail;
  const triangleSize = d ? 18 : 13;
  const headerClass = d ? 'text-sm' : 'text-[11px]';
  const underlineW = d ? 130 : 90;
  const reasonClass = d ? 'text-4xl' : 'text-2xl';
  const eyeSize = d ? 64 : 44;
  const hintClass = d ? 'text-base' : 'text-xs';
  const clusterSize = d ? 56 : 40;
  const curlSize = d ? 22 : 16;

  return (
    <div
      className={cn(
        'absolute inset-0 z-20 flex items-center justify-center cursor-pointer select-none',
        className,
      )}
      onClick={handleTap}
      role="button"
      aria-label="Tap to reveal content"
    >
      <div
        className="relative"
        style={{
          width: d ? '82%' : '86%',
          height: d ? '78%' : '82%',
          transform: `rotate(${noteRotation}deg) translate(${offsetX}%, ${offsetY}%)`,
          filter: 'drop-shadow(2px 5px 10px rgba(45,27,78,0.25))',
          backgroundColor: noteColor,
        }}
      >
        {/* ── Paper texture ──────────────────────────────────────── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '256px 256px',
          }}
        />

        {/* ── Sticky strip at top ────────────────────────────────── */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{ height: '6%', background: 'rgba(0,0,0,0.04)' }}
        />

        {/* ── Corner curl ────────────────────────────────────────── */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: curlSize,
            height: curlSize,
            bottom: 0,
            ...(curlCorner === 0 ? { right: 0 } : { left: 0 }),
            background: `linear-gradient(${
              curlCorner === 0 ? '315deg' : '225deg'
            }, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.02) 50%)`,
          }}
        />

        {/* ── Corner doodle clusters ────────────────────────────── */}
        {/* Top-left corner */}
        <div className="absolute pointer-events-none" style={{ top: 2, left: 2 }}>
          <CornerCluster index={cluster1} color={INK} size={clusterSize} />
        </div>
        {/* Bottom-right corner — same cluster flipped to mirror */}
        <div className="absolute pointer-events-none" style={{ bottom: 2, right: 2 }}>
          <CornerCluster index={cluster1} color={INK} size={clusterSize} flipX flipY />
        </div>
        {/* Top-right corner — second cluster, flipped horizontally */}
        <div className="absolute pointer-events-none" style={{ top: 2, right: 2 }}>
          <CornerCluster index={cluster2} color={INK} size={clusterSize} flipX />
        </div>
        {/* Bottom-left corner — second cluster, flipped vertically */}
        <div className="absolute pointer-events-none" style={{ bottom: 2, left: 2 }}>
          <CornerCluster index={cluster2} color={INK} size={clusterSize} flipY />
        </div>

        {/* ── Main content ───────────────────────────────────────── */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4">

          {/* ⚠ CONTENT WARNING header */}
          <div className="flex items-center gap-1">
            <WarningTriangle size={triangleSize} color={INK} />
            <span
              className={cn('font-hand font-bold tracking-wider uppercase', headerClass)}
              style={{ color: INK, letterSpacing: '0.12em' }}
            >
              CONTENT WARNING
            </span>
          </div>

          {/* Double hand-drawn underline */}
          <div className={d ? 'mt-0.5' : '-mt-0.5'}>
            <HandDrawnUnderline width={underlineW} color={INK} />
          </div>

          {/* The reason — BIG bold handwriting, the star of the note */}
          <p
            className={cn(
              'font-hand font-bold text-center leading-tight',
              reasonClass,
              d ? 'mt-2' : 'mt-1',
            )}
            style={{ color: INK }}
          >
            {label}
          </p>

          {/* Big bold eye-slash doodle */}
          <div className={d ? 'mt-3' : 'mt-1.5'}>
            <EyeSlashDoodle size={eyeSize} color={INK} />
          </div>

          {/* "tap to reveal" */}
          <span
            className={cn(
              'font-hand italic',
              hintClass,
              d ? 'mt-4' : 'mt-2',
            )}
            style={{ color: INK_MED }}
          >
            tap to reveal
          </span>
        </div>
      </div>
    </div>
  );
}
